import {
  CURRENT_SCHEMA_VERSION,
  defaultSaveBundle,
  type SaveBundle,
} from '../game/PuppyGame';

// Mirrors iOS SaveStore.swift's contract, swapping the filesystem (save.json
// in a directory) for browser localStorage. load/save/clear/legacyCleanup
// names match iOS so call sites translate 1:1.

export interface SaveStoreOptions {
  storage?: Storage;
  key?: string;
  legacyKey?: string;
}

const DEFAULT_KEY = 'puppy-parks.save.v1';
const DEFAULT_LEGACY_KEY = 'puppy-parks.legacy.puppyData';

export class SaveStore {
  private storage: Storage;
  private key: string;
  private legacyKey: string;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly debounceMs = 500;
  private pendingBundle: SaveBundle | null = null;

  constructor(opts: SaveStoreOptions = {}) {
    // Throws helpful error if used in node without a polyfill.
    const fallback = typeof localStorage !== 'undefined' ? localStorage : undefined;
    const storage = opts.storage ?? fallback;
    if (!storage) {
      throw new Error('SaveStore: no Storage available; pass opts.storage');
    }
    this.storage = storage;
    this.key = opts.key ?? DEFAULT_KEY;
    this.legacyKey = opts.legacyKey ?? DEFAULT_LEGACY_KEY;
  }

  // Returns the saved bundle, or the default bundle on any failure
  // (missing, corrupt, future schemaVersion).
  load(): SaveBundle {
    const raw = this.storage.getItem(this.key);
    if (raw === null) return defaultSaveBundle();
    try {
      const parsed = JSON.parse(raw) as SaveBundle;
      if (
        typeof parsed !== 'object' ||
        parsed === null ||
        typeof parsed.schemaVersion !== 'number' ||
        parsed.schemaVersion > CURRENT_SCHEMA_VERSION
      ) {
        return defaultSaveBundle();
      }
      return parsed;
    } catch {
      return defaultSaveBundle();
    }
  }

  // Stamps the current schemaVersion before writing.
  save(bundle: SaveBundle): void {
    const stamped: SaveBundle = { ...bundle, schemaVersion: CURRENT_SCHEMA_VERSION };
    this.storage.setItem(this.key, JSON.stringify(stamped, null, 2));
  }

  // Mirrors iOS clear(): writes a default bundle (NOT removeItem).
  clear(): void {
    this.save(defaultSaveBundle());
  }

  // Mirrors iOS legacyCleanup(): removes any pre-v1 storage entry if present.
  legacyCleanup(): void {
    if (this.storage.getItem(this.legacyKey) !== null) {
      this.storage.removeItem(this.legacyKey);
    }
  }

  // Convenience: true iff a non-default bundle is on disk. Plan 2 uses this
  // for the "show Dog Training on first launch" gate.
  hasSave(): boolean {
    return this.storage.getItem(this.key) !== null;
  }

  // Debounced save — call after every placement; coalesces within debounceMs.
  debouncedSave(bundle: SaveBundle): void {
    this.pendingBundle = bundle;
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      if (this.pendingBundle) this.save(this.pendingBundle);
      this.pendingBundle = null;
      this.debounceTimer = null;
    }, this.debounceMs);
  }

  // Force-flush any pending save. Call from visibilitychange / pagehide.
  flushSave(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.pendingBundle) {
      this.save(this.pendingBundle);
      this.pendingBundle = null;
    }
  }

  // Discard any pending debounced save without writing it. Call before clear()
  // so a New Game can't be overwritten by an in-flight save of the old game.
  cancelPendingSave(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.pendingBundle = null;
  }
}
